'use client';

import type { MouseEvent } from 'react';
import { useToast } from '@/components/ui/use-toast';
import type { ToasterToast } from '@/components/ui/use-toast';
import {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastIcon,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from '@/components/ui/toast';

/**
 * RUT-3962: for `actionOnBodyClick` toasts, a click anywhere on the toast
 * except the X runs the action. The office kept missing the small button and
 * hitting the hidden X beside it, which dismissed the toast and sent nothing.
 */
function handleBodyClick(t: ToasterToast, event: MouseEvent<HTMLLIElement>) {
  if (!t.actionOnBodyClick || !t.action) return;
  const target = event.target as HTMLElement;
  // The button runs the action itself; the X means "dismiss".
  if (target.closest('[data-toast-action], [toast-close]')) return;
  t.action.onClick();
  t.onOpenChange?.(false);
}

export function Toaster() {
  const { toasts } = useToast();

  return (
    <ToastProvider>
      {toasts.map(function (t) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, title, description, action, variant, duration, actionOnBodyClick, ...props } = t;
        return (
          <Toast
            key={id}
            variant={variant}
            duration={duration}
            {...props}
            onClick={(e) => handleBodyClick(t, e)}
          >
            <ToastIcon variant={variant} />
            <div className="grid min-w-0 flex-1 gap-1" data-toast-body="">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && <ToastDescription>{description}</ToastDescription>}
              {/* Under the text, away from the X, so a near-miss can't dismiss (RUT-3962). */}
              {action && (
                <div className="mt-1">
                  <ToastAction altText={action.altText} onClick={action.onClick} data-toast-action="">
                    {action.label}
                  </ToastAction>
                </div>
              )}
            </div>
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
